import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService } from '../../lib/college-journey-service';
import { 
  ArrowLeft, 
  ArrowRight, 
  Save,
  RefreshCw,
  User,
  Users,
  Brain,
  Target,
  BookOpen,
  Lightbulb,
  Zap,
  Palette,
  CheckCircle,
  AlertCircle,
  Eye,
  GraduationCap,
  MapPin,
  Calendar,
  Plus,
  Trash2,
  Star,
  Award
} from 'lucide-react';

const ComprehensiveStudentProfile = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('student-profile');
  const [saveStatus, setSaveStatus] = useState('saved');
  const [saveMessage, setSaveMessage] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState('');
  
  // US States for dropdown
  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];
  
  // Complete Student Academic Profile Data - matching all database fields
  const [studentProfile, setStudentProfile] = useState({
    // Basic Information
    stateOfResidency: '',
    graduationYear: '',
    highSchoolName: '',
    highSchoolState: '',
    highSchoolCity: '',
    highSchoolType: '',
    gradeLevel: '',
    
    // GPA & Academic Performance
    gpaWeighted: '',
    gpaUnweighted: '',
    gpaScale: '4.0',
    classRank: '',
    classSize: '',
    classRankPercentage: '',
    
    // Test Scores
    satTotal: '',
    satMath: '',
    satReadingWriting: '',
    satDate: '',
    satAttempts: '1',
    actComposite: '',
    actMath: '',
    actReading: '',
    actEnglish: '',
    actScience: '',
    actDate: '',
    actAttempts: '1',
    psatTotal: '',
    psatDate: '',
    nationalMeritStatus: '',
    
    // Academic Rigor
    apCoursesTaken: '',
    apCoursesPlanned: '',
    apExamsPassed: '',
    apCoursesDetails: '',
    ibDiplomaCandidate: false,
    ibPredictedScore: '',
    dualEnrollmentCredits: '',
    honorsCourses: '',
    curriculumRigor: '',
    courseLoadDescription: '',
    
    // Interests & Goals
    academicInterests: [],
    intendedMajor: '',
    intendedMinor: '',
    careerGoals: '',
    learningStyle: '',
    academicStrengths: [],
    uniqueQualities: '',
    studentSelfAssessment: '',
    
    // Background & Context
    collegePreferences: {},
    firstGenerationCollege: false,
    legacyConnections: [],
    recruitedAthleteSports: [],
    underrepresentedBackground: false,
    specialCircumstances: '',
    
    // Activities & Experience
    leadershipActivities: '',
    volunteerWork: '',
    workExperience: '',
    specialTalents: '',
    languages: '',
    additionalNotes: '',
    
    // Awards & Recognition
    academicAwards: '',
    athleticAwards: '',
    otherAwards: '',
    
    // Complex data structures for modern UI
    extracurriculars: [],
    awards: []
  });

  // Complete Parent Observations - matching all database fields
  const [parentObservationsA, setParentObservationsA] = useState({
    parentObservationEasySubject: '',
    parentObservationGrowth: '',
    parentObservationLearningDifference: '',
    parentObservationSurprises: '',
    parentObservationAgeLimit: '',
    parentObservationRulesQuestioned: '',
    parentObservationWrongLevel: '',
    parentObservationAdvocacySuccess: '',
    parentObservationInitiativeAgainstAdvice: '',
    parentObservationMechanicalSkills: '',
    parentObservationMathIntegration: '',
    parentObservationEntrepreneurialInstincts: '',
    parentObservationObsessions: '',
    parentObservationUnusualInterests: '',
    parentObservationHiddenQualities: ''
  });
  
  const [parentObservationsB, setParentObservationsB] = useState({
    parentBEasySubject: '',
    parentBGrowth: '',
    parentBLearningDifference: '',
    parentBSurprises: '',
    parentBAgeLimit: '',
    parentBRulesQuestioned: '',
    parentBWrongLevel: '',
    parentBAdvocacySuccess: '',
    parentBInitiativeAgainstAdvice: '',
    parentBMechanicalSkills: '',
    parentBMathIntegration: '',
    parentBEntrepreneurialInstincts: '',
    parentBObsessions: '',
    parentBUnusualInterests: '',
    parentBHiddenQualities: '',
    parentBDifferentPerspective: '',
    parentBWhatOtherParentMisses: ''
  });
  
  // Academic Interest Areas for customized questions
  const [selectedAcademicAreas, setSelectedAcademicAreas] = useState(['Engineering']);

  // Personality Profile
  const [personalityProfile, setPersonalityProfile] = useState({
    motivationStyle: '',
    communicationPreference: '',
    resistancePatterns: '',
    energySource: '',
    decisionMaking: '',
    feedbackStyle: '',
    identityFramework: ''
  });


  // Track the selected student ID and reload when it changes
  useEffect(() => {
    const currentSelectedId = localStorage.getItem('college-selected-student-id');
    if (currentSelectedId !== selectedStudentId) {
      setSelectedStudentId(currentSelectedId);
      loadStudentData();
    }
  }, [selectedStudentId]);
  
  // Initial load
  useEffect(() => {
    loadStudentData();
  }, [currentUser]);
  
  // Also reload when the localStorage value changes (e.g., when parent selects different student)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'college-selected-student-id') {
        console.log('🔄 Student selection changed, reloading data for:', e.newValue);
        setSelectedStudentId(e.newValue);
        
        // Also update the student name
        const studentName = localStorage.getItem('college-selected-student-name');
        setSelectedStudentName(studentName || '');
        
        loadStudentData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const loadStudentData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser.id;
      
      // Get student name for UI display
      const storedStudentName = localStorage.getItem('college-selected-student-name');
      if (storedStudentName) {
        setSelectedStudentName(storedStudentName);
      }
      
      console.log('🔍 Loading comprehensive student data for user:', userIdForQuery, 'Name:', storedStudentName);
      
      // Try to query with the primary user_id first
      let { data: profileData, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', userIdForQuery)
        .single();

      // If no data found and we have a selected student ID, try alternate approaches
      if (!profileData && selectedStudentUserId) {
        console.log('🔍 No data found with primary user_id, trying direct database search...');
        
        // Try querying without .single() to see if there are any records
        const { data: allRecords } = await supabase
          .from('student_academic_profiles')
          .select('*');
        
        console.log('🔍 All student academic profiles in database:', allRecords);
        
        // Look for records that might match the selected student
        if (allRecords && allRecords.length > 0) {
          // Try to find a match by various criteria
          const possibleMatch = allRecords.find(record => 
            record.user_id === selectedStudentUserId ||
            record.user_id === currentUser.id
          );
          
          if (possibleMatch) {
            console.log('✅ Found matching record:', possibleMatch);
            profileData = possibleMatch;
          } else {
            console.log('🔍 Available user_ids in database:', allRecords.map(r => r.user_id));
            console.log('🔍 Looking for user_id:', selectedStudentUserId);
          }
        }
      }

      if (profileData) {
        console.log('✅ Loaded student data:', profileData);
        
        // Load complete student profile data
        setStudentProfile({
          // Basic Information
          stateOfResidency: profileData.state_of_residency || '',
          graduationYear: profileData.graduation_year || '',
          highSchoolName: profileData.high_school_name || '',
          highSchoolState: profileData.high_school_state || '',
          highSchoolCity: profileData.high_school_city || '',
          highSchoolType: profileData.high_school_type || '',
          gradeLevel: profileData.grade_level || '',
          
          // GPA & Academic Performance
          gpaWeighted: profileData.gpa_weighted || '',
          gpaUnweighted: profileData.gpa_unweighted || '',
          gpaScale: profileData.gpa_scale || '4.0',
          classRank: profileData.class_rank || '',
          classSize: profileData.class_size || '',
          classRankPercentage: profileData.class_rank_percentage || '',
          
          // Test Scores
          satTotal: profileData.sat_total || '',
          satMath: profileData.sat_math || '',
          satReadingWriting: profileData.sat_reading_writing || '',
          actComposite: profileData.act_composite || '',
          actMath: profileData.act_math || '',
          actReading: profileData.act_reading || '',
          actEnglish: profileData.act_english || '',
          actScience: profileData.act_science || '',
          psatTotal: profileData.psat_total || '',
          nationalMeritStatus: profileData.national_merit_status || '',
          
          // Academic Rigor
          apCoursesTaken: profileData.ap_courses_taken || '',
          apCoursesPlanned: profileData.ap_courses_planned || '',
          apExamsPassed: profileData.ap_exams_passed || '',
          apCoursesDetails: profileData.ap_courses_details || '',
          ibDiplomaCandidate: profileData.ib_diploma_candidate || false,
          ibPredictedScore: profileData.ib_predicted_score || '',
          dualEnrollmentCredits: profileData.dual_enrollment_credits || '',
          honorsCourses: profileData.honors_courses || '',
          curriculumRigor: profileData.curriculum_rigor || '',
          courseLoadDescription: profileData.course_load_description || '',
          
          // Interests & Goals
          academicInterests: profileData.academic_interests || [],
          intendedMajor: profileData.intended_major || '',
          intendedMinor: profileData.intended_minor || '',
          careerGoals: profileData.career_goals || '',
          learningStyle: profileData.learning_style || '',
          academicStrengths: profileData.academic_strengths || [],
          uniqueQualities: profileData.unique_qualities || '',
          studentSelfAssessment: profileData.student_self_assessment || '',
          
          // Background & Context
          collegePreferences: profileData.college_preferences || {},
          firstGenerationCollege: profileData.first_generation_college || false,
          legacyConnections: profileData.legacy_connections || [],
          underrepresentedBackground: profileData.underrepresented_background || false,
          specialCircumstances: profileData.special_circumstances || '',
          
          // Activities & Experience
          leadershipActivities: profileData.leadership_activities || '',
          volunteerWork: profileData.volunteer_work || '',
          workExperience: profileData.work_experience || '',
          specialTalents: profileData.special_talents || '',
          languages: profileData.languages || '',
          additionalNotes: profileData.additional_notes || '',
          
          // Awards & Recognition
          academicAwards: profileData.academic_awards || '',
          // High School Context
          hsCompetitiveness: profileData.hs_competitiveness || '',
          hsAvgTestScores: profileData.hs_avg_test_scores || '',
          hsPctCollegeBound: profileData.hs_pct_college_bound || '',
          
          // Grade Context
          gradeTrend: profileData.grade_trend || '',
          gradeContext: profileData.grade_context || '',
          
          // Complex data structures
          extracurriculars: profileData.extracurriculars || [],
          awards: profileData.awards || []
        });

        // Load Parent A observations
        setParentObservationsA({
          parentObservationEasySubject: profileData.parent_observation_easy_subject || '',
          parentObservationGrowth: profileData.parent_observation_growth || '',
          parentObservationLearningDifference: profileData.parent_observation_learning_difference || '',
          parentObservationSurprises: profileData.parent_observation_surprises || '',
          parentObservationAgeLimit: profileData.parent_observation_age_limit || '',
          parentObservationRulesQuestioned: profileData.parent_observation_rules_questioned || '',
          parentObservationWrongLevel: profileData.parent_observation_wrong_level || '',
          parentObservationAdvocacySuccess: profileData.parent_observation_advocacy_success || '',
          parentObservationInitiativeAgainstAdvice: profileData.parent_observation_initiative_against_advice || '',
          parentObservationMechanicalSkills: profileData.parent_observation_mechanical_skills || '',
          parentObservationMathIntegration: profileData.parent_observation_math_integration || '',
          parentObservationEntrepreneurialInstincts: profileData.parent_observation_entrepreneurial_instincts || '',
          parentObservationObsessions: profileData.parent_observation_obsessions || '',
          parentObservationUnusualInterests: profileData.parent_observation_unusual_interests || '',
          parentObservationHiddenQualities: profileData.parent_observation_hidden_qualities || ''
        });
        
        // Load Parent B observations
        setParentObservationsB({
          parentBEasySubject: profileData.parent_b_easy_subject || '',
          parentBGrowth: profileData.parent_b_growth || '',
          parentBLearningDifference: profileData.parent_b_learning_difference || '',
          parentBSurprises: profileData.parent_b_surprises || '',
          parentBAgeLimit: profileData.parent_b_age_limit || '',
          parentBRulesQuestioned: profileData.parent_b_rules_questioned || '',
          parentBWrongLevel: profileData.parent_b_wrong_level || '',
          parentBAdvocacySuccess: profileData.parent_b_advocacy_success || '',
          parentBInitiativeAgainstAdvice: profileData.parent_b_initiative_against_advice || '',
          parentBMechanicalSkills: profileData.parent_b_mechanical_skills || '',
          parentBMathIntegration: profileData.parent_b_math_integration || '',
          parentBEntrepreneurialInstincts: profileData.parent_b_entrepreneurial_instincts || '',
          parentBObsessions: profileData.parent_b_obsessions || '',
          parentBUnusualInterests: profileData.parent_b_unusual_interests || '',
          parentBHiddenQualities: profileData.parent_b_hidden_qualities || '',
          parentBDifferentPerspective: profileData.parent_b_different_perspective || '',
          parentBWhatOtherParentMisses: profileData.parent_b_what_other_parent_misses || ''
        });
        
        // Load saved parent-selected academic interest areas
        if (profileData.parent_selected_academic_areas) {
          try {
            const savedAreas = JSON.parse(profileData.parent_selected_academic_areas);
            if (Array.isArray(savedAreas) && savedAreas.every(area => academicAreas.includes(area))) {
              setSelectedAcademicAreas(savedAreas);
            }
          } catch (error) {
            console.log('Error parsing parent-selected academic interest areas:', error);
          }
        }

        // Load personality profile
        if (profileData.personality_profile) {
          try {
            const savedProfile = JSON.parse(profileData.personality_profile);
            setPersonalityProfile(prev => ({ ...prev, ...savedProfile }));
          } catch (error) {
            console.log('Error parsing personality profile:', error);
          }
        }

      }
      
      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error loading student data:', error);
      }
      
      if (!profileData) {
        console.log('⚠️ No profile data found for user_id:', userIdForQuery);
        console.log('⚠️ Selected student ID from localStorage:', selectedStudentUserId);
        console.log('⚠️ Current user ID:', currentUser.id);
      }
    } catch (error) {
      console.error('❌ Error loading comprehensive student data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Personality assessment questions
  const personalityQuestions = [
    {
      id: 'motivationStyle',
      question: 'What primarily drives this student?',
      options: [
        { value: 'achievement', label: 'Achievement & Recognition - wants to excel and be acknowledged' },
        { value: 'curiosity', label: 'Curiosity & Understanding - driven to figure things out and learn' },
        { value: 'impact', label: 'Impact & Contribution - motivated by making a difference' }
      ]
    },
    {
      id: 'communicationPreference',
      question: 'How does this student best receive and process information?',
      options: [
        { value: 'data-logic', label: 'Data & Logic - responds to facts, evidence, and logical reasoning' },
        { value: 'stories-examples', label: 'Stories & Examples - learns through narratives and concrete examples' },
        { value: 'visual-hands-on', label: 'Visual & Hands-on - needs to see it or try it to understand' }
      ]
    },
    {
      id: 'resistancePatterns',
      question: 'When this student resists or pushes back, it\'s usually because:',
      options: [
        { value: 'authority-skeptical', label: 'Authority-skeptical - questions "because I said so" reasoning' },
        { value: 'perfectionist-paralyzed', label: 'Perfectionist-paralyzed - afraid of not doing it perfectly' },
        { value: 'overwhelm-avoidant', label: 'Overwhelm-avoidant - shuts down when there\'s too much at once' }
      ]
    },
    {
      id: 'energySource',
      question: 'This student gets energized by:',
      options: [
        { value: 'competition', label: 'Competition - thrives on winning and outperforming others' },
        { value: 'collaboration', label: 'Collaboration - energized by working with and helping others' },
        { value: 'independent-exploration', label: 'Independent exploration - needs space to figure things out alone' }
      ]
    },
    {
      id: 'decisionMaking',
      question: 'When making important decisions, this student typically:',
      options: [
        { value: 'research-heavy', label: 'Research-heavy - wants all the data before deciding' },
        { value: 'intuition-based', label: 'Intuition-based - goes with gut feeling and instinct' },
        { value: 'consensus-seeking', label: 'Consensus-seeking - wants input from trusted people' }
      ]
    },
    {
      id: 'feedbackStyle',
      question: 'This student responds best to feedback that is:',
      options: [
        { value: 'direct-analytical', label: 'Direct & Analytical - straightforward, data-based critique' },
        { value: 'supportive-encouraging', label: 'Supportive & Encouraging - positive reinforcement first' },
        { value: 'challenge-based', label: 'Challenge-based - framed as problems to solve' }
      ]
    },
    {
      id: 'identityFramework',
      question: 'This student sees themselves primarily as:',
      options: [
        { value: 'individual-achiever', label: 'Individual achiever - personal excellence and recognition' },
        { value: 'community-contributor', label: 'Community contributor - part of something bigger' },
        { value: 'system-innovator', label: 'System innovator - someone who improves how things work' }
      ]
    }
  ];


  const updatePersonalityProfile = (field, value) => {
    setPersonalityProfile(prev => ({ ...prev, [field]: value }));
  };


  const saveComprehensiveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      // Use selectedStudentId if available, otherwise fall back to currentUser.id
      const userIdForQuery = selectedStudentId || currentUser.id;
      
      // Debug logging for user ID investigation
      console.log('🚨 SIMPLE FIX: User ID Resolution');
      console.log('🚨 selectedStudentId from state:', selectedStudentId);
      console.log('🚨 currentUser.id (fallback):', currentUser.id);
      console.log('🚨 Final userIdForQuery:', userIdForQuery);
      console.log('🔍 Saving comprehensive student profile for user:', userIdForQuery);
      
      // Use the college journey service for proper upsert functionality
      await collegeJourneyService.updateAcademicProfile({
        // Basic Information
        state_of_residency: studentProfile.stateOfResidency,
        graduation_year: studentProfile.graduationYear ? parseInt(studentProfile.graduationYear) : null,
        high_school_name: studentProfile.highSchoolName,
        high_school_state: studentProfile.highSchoolState,
        high_school_city: studentProfile.highSchoolCity,
        high_school_type: studentProfile.highSchoolType,
        grade_level: studentProfile.gradeLevel,
        
        // GPA & Academic Performance
        gpa_weighted: studentProfile.gpaWeighted ? parseFloat(studentProfile.gpaWeighted) : null,
        gpa_unweighted: studentProfile.gpaUnweighted ? parseFloat(studentProfile.gpaUnweighted) : null,
        gpa_scale: studentProfile.gpaScale ? parseFloat(studentProfile.gpaScale) : 4.0,
        class_rank: studentProfile.classRank ? parseInt(studentProfile.classRank) : null,
        class_size: studentProfile.classSize ? parseInt(studentProfile.classSize) : null,
        
        // Test Scores
        sat_total: studentProfile.satTotal ? parseInt(studentProfile.satTotal) : null,
        sat_math: studentProfile.satMath ? parseInt(studentProfile.satMath) : null,
        sat_reading_writing: studentProfile.satReadingWriting ? parseInt(studentProfile.satReadingWriting) : null,
        act_composite: studentProfile.actComposite ? parseInt(studentProfile.actComposite) : null,
        act_math: studentProfile.actMath ? parseInt(studentProfile.actMath) : null,
        act_reading: studentProfile.actReading ? parseInt(studentProfile.actReading) : null,
        act_english: studentProfile.actEnglish ? parseInt(studentProfile.actEnglish) : null,
        act_science: studentProfile.actScience ? parseInt(studentProfile.actScience) : null,
        psat_total: studentProfile.psatTotal ? parseInt(studentProfile.psatTotal) : null,
        national_merit_status: studentProfile.nationalMeritStatus || null,
        
        // Academic Rigor
        ap_courses_taken: studentProfile.apCoursesTaken ? parseInt(studentProfile.apCoursesTaken) : null,
        ap_courses_planned: studentProfile.apCoursesPlanned ? parseInt(studentProfile.apCoursesPlanned) : null,
        ap_exams_passed: studentProfile.apExamsPassed ? parseInt(studentProfile.apExamsPassed) : null,
        ap_courses_details: studentProfile.apCoursesDetails || null,
        ib_diploma_candidate: studentProfile.ibDiplomaCandidate || false,
        ib_predicted_score: studentProfile.ibPredictedScore ? parseInt(studentProfile.ibPredictedScore) : null,
        dual_enrollment_credits: studentProfile.dualEnrollmentCredits ? parseInt(studentProfile.dualEnrollmentCredits) : null,
        honors_courses: studentProfile.honorsCourses ? parseInt(studentProfile.honorsCourses) : null,
        curriculum_rigor: studentProfile.curriculumRigor || null,
        course_load_description: studentProfile.courseLoadDescription || null,
        
        // Interests & Goals
        academic_interests: studentProfile.academicInterests || [],
        intended_major: studentProfile.intendedMajor || null,
        intended_minor: studentProfile.intendedMinor || null,
        career_goals: studentProfile.careerGoals || null,
        learning_style: studentProfile.learningStyle || null,
        academic_strengths: studentProfile.academicStrengths || [],
        unique_qualities: studentProfile.uniqueQualities || null,
        student_self_assessment: studentProfile.studentSelfAssessment || null,
        
        // Background & Context
        college_preferences: studentProfile.collegePreferences || {},
        first_generation_college: studentProfile.firstGenerationCollege || false,
        legacy_connections: studentProfile.legacyConnections || [],
        underrepresented_background: studentProfile.underrepresentedBackground || false,
        special_circumstances: studentProfile.specialCircumstances || null,
        
        // Activities & Experience
        leadership_activities: studentProfile.leadershipActivities || null,
        volunteer_work: studentProfile.volunteerWork || null,
        work_experience: studentProfile.workExperience || null,
        special_talents: studentProfile.specialTalents || null,
        languages: studentProfile.languages || null,
        additional_notes: studentProfile.additionalNotes || null,
        
        // Awards & Recognition
        academic_awards: studentProfile.academicAwards || null,
        // High School Context
        hs_competitiveness: studentProfile.hsCompetitiveness || null,
        hs_avg_test_scores: studentProfile.hsAvgTestScores || null,
        hs_pct_college_bound: studentProfile.hsPctCollegeBound ? parseInt(studentProfile.hsPctCollegeBound) : null,
        
        // Grade Context  
        grade_trend: studentProfile.gradeTrend || null,
        grade_context: studentProfile.gradeContext || null,
        
        // Complex data structures
        extracurriculars: studentProfile.extracurriculars || [],
        awards: studentProfile.awards || [],
        
        // Parent A Observations
        parent_observation_easy_subject: parentObservationsA.parentObservationEasySubject || null,
        parent_observation_growth: parentObservationsA.parentObservationGrowth || null,
        parent_observation_learning_difference: parentObservationsA.parentObservationLearningDifference || null,
        parent_observation_surprises: parentObservationsA.parentObservationSurprises || null,
        parent_observation_age_limit: parentObservationsA.parentObservationAgeLimit || null,
        parent_observation_rules_questioned: parentObservationsA.parentObservationRulesQuestioned || null,
        parent_observation_wrong_level: parentObservationsA.parentObservationWrongLevel || null,
        parent_observation_advocacy_success: parentObservationsA.parentObservationAdvocacySuccess || null,
        parent_observation_initiative_against_advice: parentObservationsA.parentObservationInitiativeAgainstAdvice || null,
        parent_observation_mechanical_skills: parentObservationsA.parentObservationMechanicalSkills || null,
        parent_observation_math_integration: parentObservationsA.parentObservationMathIntegration || null,
        parent_observation_entrepreneurial_instincts: parentObservationsA.parentObservationEntrepreneurialInstincts || null,
        parent_observation_obsessions: parentObservationsA.parentObservationObsessions || null,
        parent_observation_unusual_interests: parentObservationsA.parentObservationUnusualInterests || null,
        parent_observation_hidden_qualities: parentObservationsA.parentObservationHiddenQualities || null,
        
        // Parent B Observations
        parent_b_easy_subject: parentObservationsB.parentBEasySubject || null,
        parent_b_growth: parentObservationsB.parentBGrowth || null,
        parent_b_learning_difference: parentObservationsB.parentBLearningDifference || null,
        parent_b_surprises: parentObservationsB.parentBSurprises || null,
        parent_b_age_limit: parentObservationsB.parentBAgeLimit || null,
        parent_b_rules_questioned: parentObservationsB.parentBRulesQuestioned || null,
        parent_b_wrong_level: parentObservationsB.parentBWrongLevel || null,
        parent_b_advocacy_success: parentObservationsB.parentBAdvocacySuccess || null,
        parent_b_initiative_against_advice: parentObservationsB.parentBInitiativeAgainstAdvice || null,
        parent_b_mechanical_skills: parentObservationsB.parentBMechanicalSkills || null,
        parent_b_math_integration: parentObservationsB.parentBMathIntegration || null,
        parent_b_entrepreneurial_instincts: parentObservationsB.parentBEntrepreneurialInstincts || null,
        parent_b_obsessions: parentObservationsB.parentBObsessions || null,
        parent_b_unusual_interests: parentObservationsB.parentBUnusualInterests || null,
        parent_b_hidden_qualities: parentObservationsB.parentBHiddenQualities || null,
        parent_b_different_perspective: parentObservationsB.parentBDifferentPerspective || null,
        parent_b_what_other_parent_misses: parentObservationsB.parentBWhatOtherParentMisses || null,
        
        // Academic Interest Areas (Parent Selected)
        parent_selected_academic_areas: JSON.stringify(selectedAcademicAreas),
        
        // Personality profile
        personality_profile: JSON.stringify(personalityProfile),
        
        framework_customized: true,
        framework_customized_at: new Date().toISOString(),
        
        // Progress tracking
        assessment_phase: 1,
        updated_at: new Date().toISOString()
      }, userIdForQuery);

      setSaveStatus('saved');
      console.log('✅ Comprehensive student profile saved successfully');
      
      // Navigate to next step
      navigate('/college/student-context-generator');
      
    } catch (error) {
      console.error('Error saving comprehensive profile:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Academic area options for customized questions
  const academicAreas = [
    'Engineering',
    'Computer & Data Science',
    'Mathematics',
    'Physical & Life Sciences',
    'Psychology & Human Behavior',
    'Social Sciences & Public Policy',
    'Humanities & Philosophy',
    'Business & Finance',
    'Literature & Language',
    'Creative Arts',
    'Health & Human Services',
    'Environment & Global Studies'
  ];
  
  // Calculate completion progress
  const getCompletionStatus = () => {
    // Student Profile - core fields that indicate meaningful progress
    const studentProfileFields = [
      studentProfile.stateOfResidency, 
      studentProfile.graduationYear, 
      studentProfile.highSchoolName,
      studentProfile.gpaWeighted || studentProfile.gpaUnweighted,
      studentProfile.satTotal || studentProfile.actComposite,
      studentProfile.intendedMajor || studentProfile.careerGoals
    ];
    const studentComplete = studentProfileFields.filter(field => field && String(field).trim()).length;
    
    // Parent Observations A
    const parentObsAComplete = Object.values(parentObservationsA).filter(obs => obs && String(obs).trim()).length;
    const parentObsATotal = Object.keys(parentObservationsA).length;
    
    // Parent Observations B  
    const parentObsBComplete = Object.values(parentObservationsB).filter(obs => obs && String(obs).trim()).length;
    const parentObsBTotal = Object.keys(parentObservationsB).length;
    
    // Personality Profile
    const personalityComplete = Object.values(personalityProfile).filter(val => val).length;
    
    return {
      student: studentComplete,
      studentTotal: studentProfileFields.length,
      parentObsA: parentObsAComplete,
      parentObsATotal: parentObsATotal,
      parentObsB: parentObsBComplete,
      parentObsBTotal: parentObsBTotal,
      personality: personalityComplete,
      personalityTotal: personalityQuestions.length
    };
  };

  const completion = getCompletionStatus();
  const overallComplete = completion.student >= 4 && completion.personality === completion.personalityTotal;

  // Autosave functionality
  const handleAutoSave = async () => {
    if (!currentUser) return;
    
    setSaveStatus('saving');
    setSaveMessage('Auto-saving...');
    
    try {
      const userIdForQuery = currentUser.user_type === 'parent' && selectedStudentId 
        ? selectedStudentId 
        : currentUser.id;
      
      await collegeJourneyService.updateAcademicProfile({
        // Basic Information
        state_of_residency: studentProfile.stateOfResidency || null,
        graduation_year: studentProfile.graduationYear || null,
        high_school_name: studentProfile.highSchoolName || null,
        high_school_state: studentProfile.highSchoolState || null,
        high_school_city: studentProfile.highSchoolCity || null,
        high_school_type: studentProfile.highSchoolType || null,
        grade_level: studentProfile.gradeLevel || null,
        
        // GPA & Academic Performance
        gpa_weighted: studentProfile.gpaWeighted || null,
        gpa_unweighted: studentProfile.gpaUnweighted || null,
        sat_score: studentProfile.satScore || null,
        act_score: studentProfile.actScore || null,
        class_rank: studentProfile.classRank || null,
        class_size: studentProfile.classSize || null,
        ap_courses_taken: studentProfile.apCoursesTaken || null,
        ib_diploma: studentProfile.ibDiploma || null,
        honors_courses: studentProfile.honorsCourses || null,
        dual_enrollment_courses: studentProfile.dualEnrollmentCourses || null,
        
        // Academic Interests & Goals
        intended_major: studentProfile.intendedMajor || null,
        career_goals: studentProfile.careerGoals || null,
        academic_interests: studentProfile.academicInterests || null,
        
        // Skills & Experiences
        academic_strengths: studentProfile.academicStrengths || null,
        extracurricular_activities: studentProfile.extracurricularActivities || null,
        leadership_experiences: studentProfile.leadershipExperiences || null,
        work_experience: studentProfile.workExperience || null,
        volunteer_experience: studentProfile.volunteerExperience || null,
        special_talents: studentProfile.specialTalents || null,
        
        // Context & Background
        learning_style: studentProfile.learningStyle || null,
        learning_challenges: studentProfile.learningChallenges || null,
        personal_challenges: studentProfile.personalChallenges || null,
        family_education_history: studentProfile.familyEducationHistory || null,
        
        // College Preferences
        preferred_college_size: studentProfile.preferredCollegeSize || null,
        preferred_college_type: studentProfile.preferredCollegeType || null,
        geographic_preferences: studentProfile.geographicPreferences || null,
        financial_considerations: studentProfile.financialConsiderations || null,
        college_priorities: studentProfile.collegePriorities || null,
        
        // Additional Information
        awards_honors: studentProfile.awardsHonors || null,
        unique_circumstances: studentProfile.uniqueCircumstances || null,
        additional_information: studentProfile.additionalInformation || null,
        
        // Parent A Observations
        parent_observation_easy_subject: parentObservationsA.parentObservationEasySubject || null,
        parent_observation_growth: parentObservationsA.parentObservationGrowth || null,
        parent_observation_learning_difference: parentObservationsA.parentObservationLearningDifference || null,
        parent_observation_surprises: parentObservationsA.parentObservationSurprises || null,
        parent_observation_age_limit: parentObservationsA.parentObservationAgeLimit || null,
        parent_observation_rules_questioned: parentObservationsA.parentObservationRulesQuestioned || null,
        parent_observation_wrong_level: parentObservationsA.parentObservationWrongLevel || null,
        parent_observation_advocacy_success: parentObservationsA.parentObservationAdvocacySuccess || null,
        parent_observation_initiative_against_advice: parentObservationsA.parentObservationInitiativeAgainstAdvice || null,
        parent_observation_mechanical_skills: parentObservationsA.parentObservationMechanicalSkills || null,
        parent_observation_math_integration: parentObservationsA.parentObservationMathIntegration || null,
        parent_observation_entrepreneurial_instincts: parentObservationsA.parentObservationEntrepreneurialInstincts || null,
        parent_observation_obsessions: parentObservationsA.parentObservationObsessions || null,
        parent_observation_unusual_interests: parentObservationsA.parentObservationUnusualInterests || null,
        parent_observation_hidden_qualities: parentObservationsA.parentObservationHiddenQualities || null,
        
        // Parent B Observations
        parent_b_easy_subject: parentObservationsB.parentBEasySubject || null,
        parent_b_growth: parentObservationsB.parentBGrowth || null,
        parent_b_learning_difference: parentObservationsB.parentBLearningDifference || null,
        parent_b_surprises: parentObservationsB.parentBSurprises || null,
        parent_b_age_limit: parentObservationsB.parentBAgeLimit || null,
        parent_b_rules_questioned: parentObservationsB.parentBRulesQuestioned || null,
        parent_b_wrong_level: parentObservationsB.parentBWrongLevel || null,
        parent_b_advocacy_success: parentObservationsB.parentBAdvocacySuccess || null,
        parent_b_initiative_against_advice: parentObservationsB.parentBInitiativeAgainstAdvice || null,
        parent_b_mechanical_skills: parentObservationsB.parentBMechanicalSkills || null,
        parent_b_math_integration: parentObservationsB.parentBMathIntegration || null,
        parent_b_entrepreneurial_instincts: parentObservationsB.parentBEntrepreneurialInstincts || null,
        parent_b_obsessions: parentObservationsB.parentBObsessions || null,
        parent_b_unusual_interests: parentObservationsB.parentBUnusualInterests || null,
        parent_b_hidden_qualities: parentObservationsB.parentBHiddenQualities || null,
        parent_b_different_perspective: parentObservationsB.parentBDifferentPerspective || null,
        parent_b_what_other_parent_misses: parentObservationsB.parentBWhatOtherParentMisses || null,
        
        // Academic Interest Areas (Parent Selected)
        parent_selected_academic_areas: JSON.stringify(selectedAcademicAreas),
        
        // Personality profile
        personality_profile: JSON.stringify(personalityProfile),
        
        // Metadata
        framework_customized: true,
        framework_customized_at: new Date().toISOString(),
        
        // Progress tracking
        assessment_phase: 1,
        updated_at: new Date().toISOString()
      }, userIdForQuery);
      
      setSaveStatus('saved');
      setSaveMessage('All changes saved');
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error auto-saving profile:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to save');
      setTimeout(() => {
        setSaveStatus('saved');
        setSaveMessage('');
      }, 3000);
    }
  };
  
  // Auto-save when data changes
  useEffect(() => {
    // Only auto-save if we have a user and some data
    const hasData = Object.values(studentProfile).some(val => val) ||
                   Object.values(parentObservationsA).some(val => val) ||
                   Object.values(parentObservationsB).some(val => val) ||
                   Object.values(personalityProfile).some(val => val);
    
    if (!currentUser || !hasData || saveStatus === 'saved') {
      return;
    }
    
    const saveTimeout = setTimeout(() => {
      console.log('🔄 ComprehensiveStudentProfile: Auto-saving...');
      handleAutoSave();
    }, 2000); // Save after 2 seconds of inactivity
    
    return () => clearTimeout(saveTimeout);
  }, [studentProfile, parentObservationsA, parentObservationsB, personalityProfile, selectedAcademicAreas, saveStatus]);
  
  // Additional periodic save every 30 seconds as backup
  useEffect(() => {
    const hasData = Object.values(studentProfile).some(val => val) ||
                   Object.values(parentObservationsA).some(val => val) ||
                   Object.values(parentObservationsB).some(val => val) ||
                   Object.values(personalityProfile).some(val => val);
    
    if (!currentUser || !hasData) {
      return;
    }
    
    const interval = setInterval(() => {
      console.log('🔄 ComprehensiveStudentProfile: Periodic save...');
      handleAutoSave();
    }, 30000); // Save every 30 seconds
    
    return () => clearInterval(interval);
  }, [currentUser]);

  // Tab navigation logic - filter based on user type
  const allTabs = [
    { id: 'student-profile', label: 'Student Academic Profile' },
    { id: 'parent-observations-a', label: 'Parent Observations A' },
    { id: 'parent-observations-b', label: 'Parent Observations B' },
    { id: 'personality', label: 'Personality Profile' }
  ];
  
  // Students should only see the student profile tab
  const tabs = currentUser?.user_type === 'student' || currentUser?.type === 'student' 
    ? allTabs.filter(tab => tab.id === 'student-profile')
    : allTabs;

  // Ensure students are always on the student-profile tab
  useEffect(() => {
    if ((currentUser?.user_type === 'student' || currentUser?.type === 'student') && activeTab !== 'student-profile') {
      setActiveTab('student-profile');
    }
  }, [currentUser, activeTab]);
  
  const getNextTab = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      return tabs[currentIndex + 1];
    }
    return null;
  };
  
  const isLastTab = () => {
    return activeTab === tabs[tabs.length - 1].id;
  };
  
  const handleContinue = () => {
    if (isLastTab() && overallComplete) {
      // On the last tab and everything is complete - save and go to strategic context
      saveComprehensiveProfile();
    } else {
      // Move to next tab
      const nextTab = getNextTab();
      if (nextTab) {
        setActiveTab(nextTab.id);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading student data</h2>
          <p className="text-slate-600">Preparing comprehensive profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/college')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to College Hub
            </button>
            
            {/* Save Status Indicator */}
            {saveMessage && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                saveStatus === 'saving' ? 'bg-amber-100 text-amber-700' :
                saveStatus === 'saved' ? 'bg-emerald-100 text-emerald-700' :
                'bg-red-100 text-red-700'
              }`}>
                {saveStatus === 'saving' && (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {saveMessage}
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {saveMessage}
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    {saveMessage}
                  </>
                )}
              </div>
            )}
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full border border-slate-700 mb-4">
              <User className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white">Step 1: Complete Student & Parent Intelligence</span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Comprehensive Student Profile
              {selectedStudentName && (
                <span className="block text-2xl font-medium text-slate-600 mt-2">
                  for {selectedStudentName}
                </span>
              )}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Gather all student information and parent insights in one comprehensive step to inform the entire assessment process.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Progress Overview - Show different view for students */}
          {(currentUser?.user_type === 'student' || currentUser?.type === 'student') ? (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 mb-8 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Profile Progress</h2>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">{completion.student}/{completion.studentTotal}</div>
                <div className="text-sm text-slate-600 mt-2">Fields Completed</div>
                <div className="mt-4 w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-slate-900 h-2 rounded-full transition-all"
                    style={{ width: `${(completion.student / completion.studentTotal) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 mb-8 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Completion Progress</h2>
              <div className="grid md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{completion.student}/{completion.studentTotal}</div>
                  <div className="text-sm text-slate-600">Student Profile</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{completion.parentObsA}/{completion.parentObsATotal}</div>
                  <div className="text-sm text-slate-600">Parent Observations A</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{completion.parentObsB}/{completion.parentObsBTotal}</div>
                  <div className="text-sm text-slate-600">Parent Observations B</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{completion.personality}/{completion.personalityTotal}</div>
                  <div className="text-sm text-slate-600">Personality Profile</div>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 mb-8">
            <div className="border-b border-slate-200">
              <nav className="flex">
                {tabs.map(tab => {
                  const Icon = tab.id === 'student-profile' ? User :
                               tab.id === 'parent-observations-a' ? Users :
                               tab.id === 'parent-observations-b' ? Users :
                               tab.id === 'personality' ? Brain : User;
                  return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 text-sm font-medium border-b-2 flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'border-slate-900 text-slate-900'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-8">
              {/* Student Academic Profile Tab */}
              {activeTab === 'student-profile' && (
                <div className="space-y-8">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Basic Information
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State of Residency</label>
                        <select
                          value={studentProfile.stateOfResidency}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, stateOfResidency: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                          <option value="">Select State</option>
                          {US_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Graduation Year</label>
                        <input
                          type="number"
                          value={studentProfile.graduationYear}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, graduationYear: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="2026"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Grade Level</label>
                        <select
                          value={studentProfile.gradeLevel}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, gradeLevel: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                          <option value="">Select Grade</option>
                          <option value="9th">9th Grade</option>
                          <option value="10th">10th Grade</option>
                          <option value="11th">11th Grade</option>
                          <option value="12th">12th Grade</option>
                          <option value="Community College - 1st Year">Community College - 1st Year</option>
                          <option value="Community College - 2nd Year">Community College - 2nd Year</option>
                          <option value="Gap Year">Gap Year</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* High School Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      High School Information
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">High School Name</label>
                        <input
                          type="text"
                          value={studentProfile.highSchoolName}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, highSchoolName: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="Holly Springs High School"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">School Type</label>
                        <select
                          value={studentProfile.highSchoolType}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, highSchoolType: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                          <option value="">Select Type</option>
                          <option value="public">Public</option>
                          <option value="private">Private</option>
                          <option value="charter">Charter</option>
                          <option value="homeschool">Homeschool</option>
                          <option value="international">International</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">School City</label>
                        <input
                          type="text"
                          value={studentProfile.highSchoolCity}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, highSchoolCity: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="Holly Springs"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">School State</label>
                        <select
                          value={studentProfile.highSchoolState}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, highSchoolState: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                          <option value="">Select State</option>
                          {US_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* GPA & Class Rank */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">GPA & Class Rank</h3>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GPA Weighted</label>
                        <input
                          type="number"
                          step="0.001"
                          value={studentProfile.gpaWeighted}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, gpaWeighted: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="4.479"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GPA Unweighted</label>
                        <input
                          type="number"
                          step="0.001"
                          value={studentProfile.gpaUnweighted}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, gpaUnweighted: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="3.833"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GPA Scale</label>
                        <select
                          value={studentProfile.gpaScale}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, gpaScale: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                          <option value="4.0">4.0</option>
                          <option value="5.0">5.0</option>
                          <option value="6.0">6.0</option>
                          <option value="100">100</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Class Rank</label>
                        <input
                          type="number"
                          value={studentProfile.classRank}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, classRank: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="25"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Class Size</label>
                        <input
                          type="number"
                          value={studentProfile.classSize}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, classSize: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="525"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Test Scores */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">SAT/ACT Scores</h3>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* SAT Scores */}
                      <div className="bg-azure-50 border border-azure-200 rounded-lg p-6">
                        <h4 className="font-medium text-azure-900 mb-4">SAT Scores</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">SAT Total</label>
                            <input
                              type="number"
                              value={studentProfile.satTotal}
                              onChange={(e) => setStudentProfile(prev => ({ ...prev, satTotal: e.target.value }))}
                              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              placeholder="1390"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Math</label>
                              <input
                                type="number"
                                value={studentProfile.satMath}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, satMath: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                placeholder="710"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Reading/Writing</label>
                              <input
                                type="number"
                                value={studentProfile.satReadingWriting}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, satReadingWriting: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                placeholder="680"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Test Date</label>
                              <input
                                type="date"
                                value={studentProfile.satDate}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, satDate: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Attempts</label>
                              <select
                                value={studentProfile.satAttempts}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, satAttempts: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              >
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4+</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ACT Scores */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
                        <h4 className="font-medium text-emerald-900 mb-4">ACT Scores</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">ACT Composite</label>
                            <input
                              type="number"
                              value={studentProfile.actComposite}
                              onChange={(e) => setStudentProfile(prev => ({ ...prev, actComposite: e.target.value }))}
                              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                              placeholder="32"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Math</label>
                              <input
                                type="number"
                                value={studentProfile.actMath}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, actMath: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                placeholder="34"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Reading</label>
                              <input
                                type="number"
                                value={studentProfile.actReading}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, actReading: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                placeholder="30"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">English</label>
                              <input
                                type="number"
                                value={studentProfile.actEnglish}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, actEnglish: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                placeholder="28"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Science</label>
                              <input
                                type="number"
                                value={studentProfile.actScience}
                                onChange={(e) => setStudentProfile(prev => ({ ...prev, actScience: e.target.value }))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                placeholder="33"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Academic Rigor */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Academic Rigor</h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Curriculum Rigor</label>
                        <select
                          value={studentProfile.curriculumRigor}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, curriculumRigor: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        >
                          <option value="">Select Rigor</option>
                          <option value="most_rigorous">Most Rigorous</option>
                          <option value="very_rigorous">Very Rigorous</option>
                          <option value="rigorous">Rigorous</option>
                          <option value="moderately_rigorous">Moderately Rigorous</option>
                          <option value="less_rigorous">Less Rigorous</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Honors Courses</label>
                        <input
                          type="number"
                          value={studentProfile.honorsCourses}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, honorsCourses: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="13"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">AP Courses Taken</label>
                        <input
                          type="number"
                          value={studentProfile.apCoursesTaken}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, apCoursesTaken: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="9"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">AP Exams Passed</label>
                        <input
                          type="number"
                          value={studentProfile.apExamsPassed}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, apExamsPassed: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="6"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">AP Course Details</label>
                      <textarea
                        value={studentProfile.apCoursesDetails}
                        onChange={(e) => setStudentProfile(prev => ({ ...prev, apCoursesDetails: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                        rows="3"
                        placeholder="List specific AP courses and scores (e.g., AP Physics, AP Calc AB, etc.)"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Course Load Description</label>
                      <textarea
                        value={studentProfile.courseLoadDescription}
                        onChange={(e) => setStudentProfile(prev => ({ ...prev, courseLoadDescription: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                        rows="2"
                        placeholder="Most challenging available (except 2nd semester Junior year)"
                      />
                    </div>
                  </div>

                  {/* Academic Interests & Goals */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Academic Interests & Goals</h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Intended Major</label>
                        <input
                          type="text"
                          value={studentProfile.intendedMajor}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, intendedMajor: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="Engineering"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Career Goals</label>
                        <input
                          type="text"
                          value={studentProfile.careerGoals}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, careerGoals: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          placeholder="Civil Engineer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Background & Special Circumstances */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Background & Special Circumstances</h3>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-6">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={studentProfile.firstGenerationCollege}
                            onChange={(e) => setStudentProfile(prev => ({ ...prev, firstGenerationCollege: e.target.checked }))}
                            className="rounded border-slate-300 text-slate-600 focus:ring-slate-500"
                          />
                          <span className="text-sm text-slate-700">First Generation College Student</span>
                        </label>

                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={studentProfile.underrepresentedBackground}
                            onChange={(e) => setStudentProfile(prev => ({ ...prev, underrepresentedBackground: e.target.checked }))}
                            className="rounded border-slate-300 text-slate-600 focus:ring-slate-500"
                          />
                          <span className="text-sm text-slate-700">Underrepresented Background</span>
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Special Circumstances</label>
                        <textarea
                          value={studentProfile.specialCircumstances}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, specialCircumstances: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="3"
                          placeholder="Military Family (7 moves during school years)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Activities & Leadership */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Activities & Experience</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Leadership Activities</label>
                        <textarea
                          value={studentProfile.leadershipActivities}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, leadershipActivities: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="2"
                          placeholder="Pre-Law Club President"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Work Experience</label>
                        <textarea
                          value={studentProfile.workExperience}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, workExperience: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="2"
                          placeholder="Part-Time Job at Panera (average 25 hours/week) September 2024-Present"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Volunteer Work</label>
                        <textarea
                          value={studentProfile.volunteerWork}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, volunteerWork: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="2"
                          placeholder="Math Tutoring Elementary Students, Math Tutoring High School Students"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Special Talents</label>
                        <textarea
                          value={studentProfile.specialTalents}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, specialTalents: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="2"
                          placeholder="eSports (internationally ranked)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Awards & Recognition */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Awards & Recognition
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Academic Awards</label>
                        <textarea
                          value={studentProfile.academicAwards}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, academicAwards: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="2"
                          placeholder="DECA Regional Qualifier 2024, DECA State Qualifier 2025, National Latin Exam (Silver/Bronze), College Board National Recognition Program"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Other Awards</label>
                        <textarea
                          value={studentProfile.otherAwards}
                          onChange={(e) => setStudentProfile(prev => ({ ...prev, otherAwards: e.target.value }))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="2"
                          placeholder="Junior Marshal"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Parent Observations A Tab */}
              {activeTab === 'parent-observations-a' && (
                <div className="space-y-8">
                  {/* Comprehensive Parent A Observations */}
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Parent A Observations</h3>
                        <p className="text-slate-600 mt-1">Strategic insights that go beyond grades and test scores</p>
                      </div>
                      <div className="text-sm text-slate-500">
                        {Object.values(parentObservationsA).filter(val => val?.trim()).length}/15 completed
                      </div>
                    </div>
                    
                    {/* Academic Interest Areas Selector */}
                    <div className="mb-6 bg-azure-50 border border-azure-200 rounded-xl p-4">
                      <div>
                        <h4 className="text-sm font-semibold text-azure-900 mb-1">Academic Interest Focus (Parent View)</h4>
                        <p className="text-xs text-azure-700 mb-4">
                          Select up to 2 academic areas to customize parent observation questions. Note: Student-selected interests (if any) will carry more weight in the final assessment.
                        </p>
                        
                        {/* Selected Areas Display */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {selectedAcademicAreas.map((area, index) => (
                            <div key={area} className="inline-flex items-center gap-2 bg-azure-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                              <span className="text-xs bg-azure-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold">
                                {index + 1}
                              </span>
                              {area}
                              <button
                                onClick={() => {
                                  setSelectedAcademicAreas(selectedAcademicAreas.filter(a => a !== area));
                                  setSaveStatus('unsaved');
                                }}
                                className="ml-1 text-azure-200 hover:text-white"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {selectedAcademicAreas.length === 0 && (
                            <div className="text-azure-700 text-sm italic">No areas selected</div>
                          )}
                        </div>
                        
                        {/* Add Area Selector */}
                        {selectedAcademicAreas.length < 2 && (
                          <div className="flex items-center gap-2">
                            <select
                              value=""
                              onChange={(e) => {
                                if (e.target.value && !selectedAcademicAreas.includes(e.target.value)) {
                                  setSelectedAcademicAreas([...selectedAcademicAreas, e.target.value]);
                                  setSaveStatus('unsaved');
                                }
                              }}
                              className="bg-white border border-azure-300 rounded-lg px-3 py-2 text-sm text-azure-900 focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                            >
                              <option value="">+ Add academic interest area</option>
                              {academicAreas
                                .filter(area => !selectedAcademicAreas.includes(area))
                                .map(area => (
                                  <option key={area} value={area}>{area}</option>
                                ))}
                            </select>
                            <span className="text-xs text-azure-700">
                              ({2 - selectedAcademicAreas.length} more allowed)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Define comprehensive questions inline */}
                    {(() => {
                      // Customized questions based on parent-selected academic areas
                      const getCustomizedQuestions = (academicAreas) => {
                        // Use the first selected area as primary, or fall back to Engineering
                        const primaryArea = academicAreas.length > 0 ? academicAreas[0] : 'Engineering';
                        
                        const practicalSkillsVariations = {
                          'Engineering': {
                            question: "What practical skills or mechanical abilities does your child have that surprise you?",
                            placeholder: "Building, fixing, troubleshooting, understanding how things work, taking apart devices..."
                          },
                          'Computer & Data Science': {
                            question: "What technical skills or computational thinking does your child demonstrate?",
                            placeholder: "Programming, debugging, understanding systems, finding patterns in data..."
                          },
                          'Mathematics': {
                            question: "What mathematical reasoning or problem-solving abilities does your child show?",
                            placeholder: "Mental math, pattern recognition, geometric thinking, logical reasoning..."
                          },
                          'Physical & Life Sciences': {
                            question: "What scientific observation or experimental skills does your child have?",
                            placeholder: "Asking 'why' questions, forming hypotheses, systematic observation..."
                          },
                          'Psychology & Human Behavior': {
                            question: "What insights into human behavior or psychology does your child demonstrate?",
                            placeholder: "Reading people, understanding motivations, predicting reactions..."
                          },
                          'Social Sciences & Public Policy': {
                            question: "What understanding of social systems or civic issues does your child show?",
                            placeholder: "Questioning policies, understanding societal patterns, civic engagement..."
                          },
                          'Humanities & Philosophy': {
                            question: "What philosophical or analytical thinking does your child display?",
                            placeholder: "Deep questions, ethical reasoning, connecting historical patterns..."
                          },
                          'Business & Finance': {
                            question: "What business sense or financial understanding does your child have?",
                            placeholder: "Entrepreneurial thinking, understanding money, strategic planning..."
                          },
                          'Literature & Language': {
                            question: "What linguistic abilities or literary insights does your child show?",
                            placeholder: "Playing with words, understanding subtext, analyzing stories..."
                          },
                          'Creative Arts': {
                            question: "What artistic abilities or creative expression does your child demonstrate?",
                            placeholder: "Visual arts, music, creative problem-solving, aesthetic sense..."
                          },
                          'Health & Human Services': {
                            question: "What care-giving or health-related insights does your child show?",
                            placeholder: "Helping others, understanding wellness, empathy in action..."
                          },
                          'Environment & Global Studies': {
                            question: "What environmental awareness or global thinking does your child demonstrate?",
                            placeholder: "Sustainability consciousness, understanding interconnections, cultural awareness..."
                          }
                        };

                        const analyticalThinkingVariations = {
                          'Engineering': {
                            question: "When does your child bring mathematical thinking into unexpected situations or conversations?",
                            placeholder: "Times they apply math/logic outside of academic contexts, engineering solutions..."
                          },
                          'Computer & Data Science': {
                            question: "When does your child bring computational or algorithmic thinking to everyday situations?",
                            placeholder: "Breaking down problems into steps, finding patterns, optimizing processes..."
                          },
                          'Mathematics': {
                            question: "When does your child apply mathematical concepts to non-math situations?",
                            placeholder: "Using statistics in sports, geometric thinking in art, logical reasoning in arguments..."
                          },
                          'Physical & Life Sciences': {
                            question: "When does your child bring scientific thinking or experimentation to everyday life?",
                            placeholder: "Testing hypotheses, making observations, questioning how things work..."
                          },
                          'Psychology & Human Behavior': {
                            question: "When does your child analyze behavior patterns or social dynamics?",
                            placeholder: "Understanding group dynamics, predicting reactions, analyzing motivations..."
                          },
                          'Social Sciences & Public Policy': {
                            question: "When does your child analyze social systems or political situations?",
                            placeholder: "Understanding cause and effect in society, questioning policies, seeing patterns..."
                          },
                          'Humanities & Philosophy': {
                            question: "When does your child bring philosophical or critical thinking to everyday situations?",
                            placeholder: "Asking deep questions, making connections across ideas, ethical reasoning..."
                          },
                          'Business & Finance': {
                            question: "When does your child apply business or strategic thinking to situations?",
                            placeholder: "Optimizing systems, understanding value creation, strategic planning..."
                          },
                          'Literature & Language': {
                            question: "When does your child bring literary analysis or language skills to life?",
                            placeholder: "Understanding subtext in conversations, analyzing stories, wordplay..."
                          },
                          'Creative Arts': {
                            question: "When does your child bring artistic or creative thinking to problem-solving?",
                            placeholder: "Creative solutions, aesthetic considerations, artistic expression..."
                          },
                          'Health & Human Services': {
                            question: "When does your child bring health awareness or care-giving instincts to situations?",
                            placeholder: "Noticing when others need help, understanding wellness, empathetic responses..."
                          },
                          'Environment & Global Studies': {
                            question: "When does your child bring environmental or global awareness to situations?",
                            placeholder: "Sustainability thinking, understanding interconnections, cultural sensitivity..."
                          }
                        };

                        // If two areas are selected, blend the questions to acknowledge both
                        if (academicAreas.length === 2) {
                          const secondaryArea = academicAreas[1];
                          
                          return {
                            practicalSkills: {
                              question: practicalSkillsVariations[primaryArea]?.question || practicalSkillsVariations['Engineering'].question,
                              placeholder: `${practicalSkillsVariations[primaryArea]?.placeholder || ''} Also consider skills related to ${secondaryArea.toLowerCase()}...`
                            },
                            analyticalThinking: {
                              question: analyticalThinkingVariations[primaryArea]?.question || analyticalThinkingVariations['Engineering'].question,
                              placeholder: `${analyticalThinkingVariations[primaryArea]?.placeholder || ''} Also consider how they apply ${secondaryArea.toLowerCase()} thinking...`
                            }
                          };
                        }
                        
                        return {
                          practicalSkills: practicalSkillsVariations[primaryArea] || practicalSkillsVariations['Engineering'],
                          analyticalThinking: analyticalThinkingVariations[primaryArea] || analyticalThinkingVariations['Engineering']
                        };
                      };
                      
                      const customizedQuestions = getCustomizedQuestions(selectedAcademicAreas);
                      
                      const parentQuestions = [
                        { id: 'parentObservationEasySubject', question: "What subject does your child find surprisingly easy that others struggle with?", placeholder: "E.g., Math concepts click immediately, or they understand complex literature without effort...", section: 'Academic & Learning Profile' },
                        { id: 'parentObservationGrowth', question: "Where have you seen the biggest academic growth or breakthrough?", placeholder: "E.g., Struggled with writing until 10th grade, then suddenly started writing beautifully...", section: 'Academic & Learning Profile' },
                        { id: 'parentObservationLearningDifference', question: "When has your child's way of thinking or learning been different from what schools typically expect?", placeholder: "Times when their approach didn't match standard methods but still worked...", section: 'System Interactions & Edge Cases' },
                        { id: 'parentObservationSurprises', question: "What does your child do that consistently surprises adults (teachers, coaches, family friends)?", placeholder: "Behaviors, insights, or abilities that catch adults off guard...", section: 'System Interactions & Edge Cases' },
                        { id: 'parentObservationAgeLimit', question: "Has your child ever been told they couldn't do something due to age/grade level, but you suspected they could handle it?", placeholder: "Times when age restrictions seemed arbitrary given their capabilities...", section: 'System Interactions & Edge Cases' },
                        { id: 'parentObservationRulesQuestioned', question: "What rules or systems has your child questioned that made you think 'actually, they have a point'?", placeholder: "Legitimate challenges to authority or conventional wisdom...", section: 'System Interactions & Edge Cases' },
                        { id: 'parentObservationWrongLevel', question: "Has your child ever been placed in the 'wrong' level for something, either too easy or too hard?", placeholder: "E.g., Reading at college level but placed in grade-level English, or struggled in advanced math despite being capable...", section: 'System Interactions & Edge Cases' },
                        { id: 'parentObservationAdvocacySuccess', question: "Describe a time your child advocated for themselves or others and succeeded in changing an outcome.", placeholder: "Times when they pushed back against decisions and proved they were right...", section: 'System Interactions & Edge Cases' },
                        { id: 'parentObservationInitiativeAgainstAdvice', question: "When has your child taken initiative on something despite being advised against it? What was the outcome?", placeholder: "Times they chose their own path despite concerns from parents/teachers...", section: 'Natural Tendencies & Uniqueness' },
                        { id: 'parentObservationMechanicalSkills', question: customizedQuestions.practicalSkills.question, placeholder: customizedQuestions.practicalSkills.placeholder, section: 'Natural Tendencies & Uniqueness' },
                        { id: 'parentObservationMathIntegration', question: customizedQuestions.analyticalThinking.question, placeholder: customizedQuestions.analyticalThinking.placeholder, section: 'Natural Tendencies & Uniqueness' },
                        { id: 'parentObservationEntrepreneurialInstincts', question: "When has your child shown entrepreneurial instincts or figured out how to create/optimize value in social or economic situations?", placeholder: "Times they identified opportunities, created businesses, optimized trades, or applied strategic thinking to social systems...", section: 'Natural Tendencies & Uniqueness' },
                        { id: 'parentObservationObsessions', question: "What does your child get obsessed with or spend way too much time on?", placeholder: "Areas of intense focus or deep interest...", section: 'Natural Tendencies & Uniqueness' },
                        { id: 'parentObservationUnusualInterests', question: "What's something your child cares deeply about that doesn't seem typical for their age/peer group?", placeholder: "Interests or values that set them apart from peers...", section: 'Natural Tendencies & Uniqueness' },
                        { id: 'parentObservationHiddenQualities', question: "What do you hope college admissions officers will understand about your child that might not show up in grades/scores?", placeholder: "Important qualities that traditional metrics don't capture...", section: 'Natural Tendencies & Uniqueness' }
                      ];
                      
                      // Group questions by section and render
                      const sections = ['Academic & Learning Profile', 'System Interactions & Edge Cases', 'Natural Tendencies & Uniqueness'];
                      
                      return (
                        <div className="space-y-6">
                          {sections.map((sectionName) => {
                            const sectionQuestions = parentQuestions.filter(q => q.section === sectionName);
                            const sectionCompleted = sectionQuestions.filter(q => parentObservationsA[q.id]?.trim()).length;
                            
                            return (
                              <div key={sectionName} className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-lg font-semibold text-slate-900">{sectionName}</h4>
                                  <span className="text-sm text-slate-500">
                                    {sectionCompleted}/{sectionQuestions.length} completed
                                  </span>
                                </div>
                                
                                <div className="space-y-6">
                                  {sectionQuestions.map((q) => {
                                    const isCustomized = q.id === 'parentObservationMechanicalSkills' || q.id === 'parentObservationMathIntegration';
                                    
                                    return (
                                      <div key={q.id}>
                                        <div className="flex items-center gap-2 mb-2">
                                          <label className="block text-sm font-medium text-slate-700">
                                            {q.question}
                                          </label>
                                          {isCustomized && selectedAcademicAreas.length > 0 && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-azure-100 text-azure-800">
                                              Tailored to {selectedAcademicAreas.length === 1 
                                                ? selectedAcademicAreas[0] 
                                                : selectedAcademicAreas.join(' + ')
                                              }
                                            </span>
                                          )}
                                        </div>
                                        <textarea
                                          value={parentObservationsA[q.id] || ''}
                                          onChange={(e) => {
                                            setParentObservationsA(prev => ({ ...prev, [q.id]: e.target.value }));
                                            setSaveStatus('unsaved');
                                          }}
                                          className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none bg-white"
                                          rows="3"
                                          placeholder={q.placeholder}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  
                </div>
              )}

              {/* Parent Observations B Tab */}
              {activeTab === 'parent-observations-b' && (
                <div className="space-y-8">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Eye className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-amber-900 mb-2">Second Parent Perspective</h3>
                        <p className="text-amber-700 text-sm">
                          These observations will complement the first parent's perspective to create a more comprehensive strategic assessment. You can complete these in any order.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Academic & Learning Profile */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-6">Academic & Learning Profile</h3>
                    
                    <div className="space-y-6">
                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What subject or activity does your child find surprisingly easy that others struggle with?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBEasySubject}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBEasySubject: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Think about natural strengths that seem effortless for them..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Describe a time when you watched your child grow through a challenge or setback.
                        </label>
                        <textarea
                          value={parentObservationsB.parentBGrowth}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBGrowth: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Focus on resilience, problem-solving, or character development..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* System Interactions & Edge Cases */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-6">System Interactions & Edge Cases</h3>
                    
                    <div className="space-y-6">
                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          When has your child's way of thinking or learning been different from what schools typically expect?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBLearningDifference}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBLearningDifference: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Times when their approach didn't match standard methods but still worked..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What does your child do that consistently surprises adults (teachers, coaches, family friends)?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBSurprises}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBSurprises: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Behaviors, insights, or abilities that catch adults off guard..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Has your child ever been told they couldn't do something due to age/grade level, but you suspected they could handle it?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBAgeLimit}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBAgeLimit: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Times when age restrictions seemed arbitrary given their capabilities..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What rules or systems has your child questioned that made you think 'actually, they have a point'?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBRulesQuestioned}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBRulesQuestioned: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Legitimate challenges to authority or conventional wisdom..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Has your child ever been placed in the 'wrong' level for something, either too easy or too hard?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBWrongLevel}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBWrongLevel: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="E.g., Reading at college level but placed in grade-level English, or struggled in advanced math despite being capable..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Describe a time your child advocated for themselves or others and succeeded in changing an outcome.
                        </label>
                        <textarea
                          value={parentObservationsB.parentBAdvocacySuccess}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBAdvocacySuccess: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Times when they pushed back against decisions and proved they were right..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Natural Tendencies & Uniqueness */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-6">Natural Tendencies & Uniqueness</h3>
                    
                    <div className="space-y-6">
                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          When has your child taken initiative on something despite being advised against it? What was the outcome?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBInitiativeAgainstAdvice}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBInitiativeAgainstAdvice: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Times they chose their own path despite concerns from parents/teachers..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What practical skills or mechanical abilities does your child have that surprise you?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBMechanicalSkills}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBMechanicalSkills: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Building, fixing, troubleshooting, understanding how things work..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          When does your child bring mathematical thinking into unexpected situations or conversations?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBMathIntegration}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBMathIntegration: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Times they apply math/logic outside of academic contexts..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          When has your child shown entrepreneurial instincts or figured out how to create/optimize value in social or economic situations?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBEntrepreneurialInstincts}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBEntrepreneurialInstincts: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Times they identified opportunities, created businesses, optimized trades, or applied strategic thinking to social systems..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What does your child get obsessed with or spend way too much time on?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBObsessions}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBObsessions: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Areas of intense focus or deep interest..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What's something your child cares deeply about that doesn't seem typical for their age/peer group?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBUnusualInterests}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBUnusualInterests: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Interests or values that set them apart from peers..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What do you hope college admissions officers will understand about your child that might not show up in grades/scores?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBHiddenQualities}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBHiddenQualities: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Important qualities that traditional metrics don't capture..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Parental Perspectives */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-6">Parental Perspectives</h3>
                    
                    <div className="space-y-6">
                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Where do you and your spouse see your child differently?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBDifferentPerspective}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBDifferentPerspective: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Different opinions about their strengths, challenges, or potential..."
                        />
                      </div>

                      <div className="border-l-4 border-slate-200 pl-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          What does your spouse miss about your child that you see clearly?
                        </label>
                        <textarea
                          value={parentObservationsB.parentBWhatOtherParentMisses}
                          onChange={(e) => {
                            setParentObservationsB(prev => ({ ...prev, parentBWhatOtherParentMisses: e.target.value }));
                            setSaveStatus('unsaved');
                          }}
                          className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          placeholder="Insights or qualities that only you seem to notice..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Personality Profile Tab */}
              {activeTab === 'personality' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Personality Assessment</h3>
                    <p className="text-slate-600 mb-6">
                      Understanding your student's personality helps create more targeted and effective assessment strategies.
                    </p>
                  </div>

                  {personalityQuestions.map((question, index) => (
                    <div key={question.id} className="bg-slate-50 rounded-xl p-6">
                      <h4 className="text-base font-semibold text-slate-900 mb-4">
                        {index + 1}. {question.question}
                      </h4>
                      
                      <div className="space-y-3">
                        {question.options.map((option) => (
                          <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              name={question.id}
                              value={option.value}
                              checked={personalityProfile[question.id] === option.value}
                              onChange={(e) => updatePersonalityProfile(question.id, e.target.value)}
                              className="mt-1 w-4 h-4 text-slate-600 border-slate-300 focus:ring-slate-500"
                            />
                            <span className="text-sm text-slate-700 leading-relaxed">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/college')}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to College Hub
            </button>

            <div className="flex items-center gap-3">
              {/* Manual Save Button */}
              {saveStatus === 'unsaved' && (
                <button
                  onClick={handleAutoSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  Save Progress
                </button>
              )}

              {/* Only show navigation buttons for parents */}
              {(currentUser?.user_type !== 'student' && currentUser?.type !== 'student') && (() => {
                const nextTab = getNextTab();
                const canContinue = isLastTab() ? overallComplete : true; // Can always move to next tab, but need completion to finish
                
                if (!canContinue && isLastTab()) {
                  return (
                    <div className="text-center">
                      <p className="text-slate-500 text-sm mb-2">
                        Complete all sections to proceed
                      </p>
                      <div className="flex items-center gap-2 text-amber-600">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">Missing: Basic Profile and Personality Assessment required</span>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <button
                    onClick={handleContinue}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving Profile...
                      </>
                    ) : isLastTab() && overallComplete ? (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        Save & Continue to Strategic Context
                      </>
                    ) : nextTab ? (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        Continue to {nextTab.label}
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        Continue
                      </>
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveStudentProfile;